import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './ModelsList.scss';
import { Breadcrumbs, ErrorMessage, Loader, Page, PageHeader } from 'components';
import { ErrorType, LoadState } from 'enums';
import { ModelsListItem } from './ModelsListItem';
import { DeviceInfoService, DevicesService } from 'services';
import { useCancelablePromise } from 'hooks';
import { IDeviceModel, IManufacturer } from 'interfaces';

export function ModelsList() {
    const { t, i18n } = useTranslation();
    const { cancelablePromise } = useCancelablePromise();

    const [models, setModels] = useState<IDeviceModel[]>([]);
    const [manufacturers, setManufacturers] = useState<{ [id: string]: IManufacturer; }>();
    const [infoLoadState, setInfoLoadState] = useState<LoadState>(LoadState.Loading);
    const [infoError, setInfoError] = useState('');

    // Load models the first time we render
    useEffect(() => {
        cancelablePromise(DeviceInfoService.getManufacturerMap())
            .then(() => {
                setManufacturers(DeviceInfoService.allManufacturersById);
                setModels(DeviceInfoService.allModels);
                setInfoLoadState(LoadState.Success);
            })
            .catch((e) => {
                setInfoLoadState(LoadState.Error);
                setInfoError(e + '')
            });
    }, []);

    const tableHeaders = (
        <div className="modelsHeaders">
            <div className="modelsHeader modelsHeaders_addNew">
                <Link to="/models/new" title={t('modelsList.headers.addNewTooltip')} data-cy="models_addNewLink">
                    {t('modelsList.headers.addNew')}
                    <FontAwesomeIcon icon="plus" className="modelsHeaders_addNewIcon" />
                </Link>
            </div>
        </div>
    );

    let content: ReactNode;
    switch (infoLoadState) {
        case LoadState.Loading:
        case LoadState.Success:
            if (models.length > 0 || infoLoadState === LoadState.Loading) {
                content = (<>
                    <ul className="modelsList" data-cy="models_deviceList">
                        {models.map(model => (
                            <ModelsListItem model={model} manufacturer={manufacturers[model.manufacturer_id].name} key={model.id} />
                        ))}
                    </ul>
                    {infoLoadState === LoadState.Loading && (
                        <Loader />
                    )}
                </>);
            } else {
                if (DevicesService.filters.length === 0) {
                    content = (
                        <div className="modelsList_emptyAddNew">
                            <div className="modelsList_addNewArrow">
                                <FontAwesomeIcon icon="reply" />
                            </div>
                            {t('modelsList.emptyMessageAddNew')}
                        </div>
                    );
                } 
            }
            break;
        case LoadState.Error:
            content = (
                <ErrorMessage type={ErrorType.Generic} message={infoError || DevicesService.loadErrorMessage} />
            );
            break;
    }

    const breadcrumbs = (
        <Breadcrumbs crumbs={[
            {
                label: t('devicesList.title'),
                url: '/',
            },
        ]} 
        dataCy="edit_breadcrumb"
        />
    );

    return (
        <Page>
            <PageHeader            
                top={breadcrumbs}
                title={t('modelsList.title')}
            />
            {tableHeaders}
            {content}
        </Page>
    );
}